<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/70 backdrop-blur-md p-3 sm:p-6 overflow-y-auto"
    @click.self="emit('close')"
  >
    <div
      class="bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200/90 dark:border-[#263449] w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col transform transition-all duration-300 ease-out"
      :class="{
        'scale-100 opacity-100': isOpen,
        'scale-95 opacity-0': !isOpen,
      }"
    >
      <!-- Top Subtle Gradient Accent Line -->
      <div class="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 shrink-0"></div>

      <!-- Professional Modal Header Bar -->
      <div
        class="bg-white dark:bg-[#111C30] px-6 py-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between shrink-0"
      >
        <div>
          <div class="flex items-center gap-2.5 flex-wrap">
            <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
              Edit Faculty Profile
            </h2>
            <span
              v-if="localTeacher?.attendanceID"
              class="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/50"
            >
              ID: {{ localTeacher.attendanceID }}
            </span>
          </div>
          <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-normal">
            Modifying credentials for {{ localTeacher.name || "Educator" }}
          </p>
        </div>

        <button
          @click="emit('close')"
          class="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors"
          title="Close Modal"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Form Container -->
      <form
        @submit.prevent="handleSave"
        class="flex flex-col flex-1 overflow-hidden"
      >
        <!-- Scrollable Form Body -->
        <div class="overflow-y-auto p-5 sm:p-7 space-y-6 flex-1 bg-slate-50/50 dark:bg-[#0B1120]">
          <!-- Section 1: Personal Information -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <User class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Personal Information</span>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              <!-- Name -->
              <div>
                <label
                  for="name"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Full Name <span class="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  v-model="localTeacher.name"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="e.g. Dr. John Doe"
                  required
                />
              </div>

              <!-- Email -->
              <div>
                <label
                  for="email"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Email Address <span class="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  v-model="localTeacher.email"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="e.g. teacher@school.edu"
                  required
                />
              </div>

              <!-- Phone -->
              <div>
                <label
                  for="phone"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Phone Number <span class="text-rose-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  v-model="localTeacher.phone"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="e.g. +91 9876543210"
                  required
                />
              </div>

              <!-- Date of Birth -->
              <div>
                <label
                  for="dob"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Date of Birth
                </label>
                <input
                  type="date"
                  id="dob"
                  :value="formatDateForInput(localTeacher.dob)"
                  @input="localTeacher.dob = $event.target.value"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                />
              </div>

              <!-- Role -->
              <div>
                <label
                  for="role"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Staff Role
                </label>
                <select
                  id="role"
                  v-model="localTeacher.role"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                >
                  <option value="teacher">Teacher</option>
                </select>
              </div>

              <!-- Subject -->
              <div>
                <label
                  for="subject"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Primary Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  v-model="localTeacher.subject"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="e.g. Mathematics, Science"
                />
              </div>

              <!-- Status -->
              <div>
                <label
                  for="status"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Account Status
                </label>
                <select
                  id="status"
                  v-model="localTeacher.status"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                >
                  <option value="active">Active</option>
                  <option value="passout">Inactive</option>
                  <option value="leaved">Leaved</option>
                </select>
              </div>

              <!-- Photo Upload -->
              <div>
                <label
                  for="photo"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Profile Photo
                </label>
                <input
                  type="file"
                  id="photo"
                  @change="handlePhotoUpload"
                  class="block w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                  accept="image/*"
                />
                <div
                  v-if="photoPreview || localTeacher.photo"
                  class="mt-2.5 flex items-center gap-3 bg-slate-50 dark:bg-[#0F172A] p-2 rounded-xl border border-slate-200/80 dark:border-[#263449]"
                >
                  <img
                    v-if="photoPreview"
                    :src="photoPreview"
                    alt="New Photo Preview"
                    class="w-10 h-10 rounded-xl object-cover ring-2 ring-indigo-300 shadow-2xs"
                  />
                  <img
                    v-else-if="localTeacher.photo"
                    :src="localTeacher.photo"
                    alt="Current Photo"
                    class="w-10 h-10 rounded-xl object-cover ring-2 ring-slate-200 shadow-2xs"
                  />
                  <span
                    v-if="photoPreview"
                    class="text-xs text-indigo-700 dark:text-indigo-400 font-semibold"
                  >
                    New Photo Selected
                  </span>
                  <span
                    v-else-if="localTeacher.photo"
                    class="text-xs text-slate-600 dark:text-slate-400 font-medium"
                  >
                    Current Photo
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 2: Aadhaar Details -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <Shield class="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span>Identity Verification</span>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              <!-- Aadhaar Number -->
              <div>
                <label
                  for="aadhaarNumber"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Aadhaar Number
                </label>
                <input
                  type="text"
                  id="aadhaarNumber"
                  v-model="localTeacher.aadhaarNumber"
                  class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="12-digit Aadhaar Number"
                />
              </div>

              <!-- Aadhaar Card Upload -->
              <div>
                <label
                  for="aadhaarCard"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Aadhaar Card Document
                </label>
                <input
                  type="file"
                  id="aadhaarCard"
                  @change="handleAadhaarUpload"
                  class="block w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
                  accept=".pdf,image/*"
                />
                <div
                  v-if="localTeacher.aadhaarCard"
                  class="mt-2.5 flex items-center gap-2"
                >
                  <Paperclip class="w-4 h-4 text-purple-500 shrink-0" />
                  <a
                    :href="localTeacher.aadhaarCard"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-purple-700 dark:text-purple-400 hover:underline font-semibold"
                  >
                    {{
                      localTeacher.aadhaarCard.startsWith("data:")
                        ? "New Document Selected"
                        : "Current Document"
                    }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 3: Address Details -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <MapPin class="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span>Address Details</span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <!-- Post Office -->
              <div>
                <label
                  for="postOffice"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Post Office
                </label>
                <input
                  type="text"
                  id="postOffice"
                  v-model="localTeacher.postOffice"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="Post Office"
                />
              </div>

              <!-- District -->
              <div>
                <label
                  for="subDistrict"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  District
                </label>
                <input
                  type="text"
                  id="subDistrict"
                  v-model="localTeacher.subDistrict"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="District"
                />
              </div>

              <!-- State -->
              <div>
                <label
                  for="state"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  v-model="localTeacher.state"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="State"
                />
              </div>

              <!-- Pincode -->
              <div>
                <label
                  for="pincode"
                  class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                >
                  Pincode
                </label>
                <input
                  type="text"
                  id="pincode"
                  v-model="localTeacher.pincode"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  placeholder="6-digit PIN"
                />
              </div>
            </div>
          </div>

          <!-- Section 4: Qualifications -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-5">
            <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-[#1E293B]">
              <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider">
                <GraduationCap class="w-4 h-4 text-teal-600 dark:text-teal-400" />
                <span>Qualifications & Degrees</span>
              </div>
              <button
                type="button"
                @click="addQualification"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-teal-50 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300 hover:bg-teal-100 border border-teal-200 dark:border-teal-800 transition shadow-2xs"
              >
                <PlusCircle class="w-3.5 h-3.5" />
                <span>Add Qualification</span>
              </button>
            </div>

            <div
              v-if="localTeacher.qualifications && localTeacher.qualifications.length > 0"
              class="space-y-4"
            >
              <div
                v-for="(q, index) in localTeacher.qualifications"
                :key="index"
                class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-4 border border-slate-200/80 dark:border-[#263449] rounded-xl bg-slate-50/70 dark:bg-[#0F172A]"
              >
                <div>
                  <label
                    :for="`q-type-${index}`"
                    class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1"
                  >
                    Degree / Certificate
                  </label>
                  <input
                    type="text"
                    :id="`q-type-${index}`"
                    v-model="q.type"
                    class="w-full px-3 py-1.5 border border-slate-300 dark:border-[#263449] rounded-lg text-xs bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    placeholder="e.g. B.Ed, M.Sc"
                  />
                </div>
                <div>
                  <label
                    :for="`q-institution-${index}`"
                    class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1"
                  >
                    University / Institution
                  </label>
                  <input
                    type="text"
                    :id="`q-institution-${index}`"
                    v-model="q.institution"
                    class="w-full px-3 py-1.5 border border-slate-300 dark:border-[#263449] rounded-lg text-xs bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    placeholder="e.g. University Name"
                  />
                </div>
                <div>
                  <label
                    :for="`q-year-${index}`"
                    class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1"
                  >
                    Completion Year
                  </label>
                  <input
                    type="text"
                    :id="`q-year-${index}`"
                    v-model="q.year"
                    class="w-full px-3 py-1.5 border border-slate-300 dark:border-[#263449] rounded-lg text-xs bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    placeholder="e.g. 2020"
                  />
                </div>
                <div class="flex items-end gap-2">
                  <div class="flex-1 min-w-0">
                    <label
                      :for="`q-file-${index}`"
                      class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1"
                    >
                      Certificate File
                    </label>
                    <input
                      type="file"
                      :id="`q-file-${index}`"
                      @change="(event) => handleQualificationUpload(event, index)"
                      class="block w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 cursor-pointer"
                      accept=".pdf,image/*"
                    />
                    <div
                      v-if="q.fileUrl"
                      class="mt-1.5 flex items-center gap-1.5"
                    >
                      <Paperclip class="w-3.5 h-3.5 text-teal-600 shrink-0" />
                      <a
                        :href="q.fileUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-[11px] text-teal-700 dark:text-teal-400 hover:underline font-medium truncate"
                      >
                        {{
                          q.fileUrl.startsWith("data:")
                            ? "New File Selected"
                            : "Current File"
                        }}
                      </a>
                    </div>
                  </div>
                  <button
                    type="button"
                    @click="removeQualification(index)"
                    class="p-2 text-rose-500 hover:text-rose-700 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors shrink-0"
                    title="Remove Qualification"
                  >
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div
              v-else
              class="py-6 text-center text-xs text-slate-400 border border-dashed border-slate-200 dark:border-[#263449] rounded-xl"
            >
              No qualifications added yet. Click "+ Add Qualification" above.
            </div>
          </div>

          <!-- Section 5: Remarks -->
          <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-3">
            <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-2 border-b border-slate-100 dark:border-[#1E293B]">
              <MessageSquare class="w-4 h-4 text-rose-600 dark:text-rose-400" />
              <span>Remarks & Administrative Notes</span>
            </div>
            <div>
              <textarea
                id="remark"
                v-model="localTeacher.remark"
                rows="3"
                class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition resize-none"
                placeholder="Add any administrative remarks or performance notes..."
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Sticky Footer Action Buttons -->
        <div
          class="p-4 sm:p-5 border-t border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#111C30] flex items-center justify-end gap-2.5 shrink-0"
        >
          <button
            type="button"
            @click="emit('close')"
            :disabled="isLoading"
            class="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isLoading"
            class="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition disabled:opacity-50"
          >
            <span
              v-if="isLoading"
              class="flex items-center gap-2"
            >
              <AppLoader variant="button" class="w-4 h-4 text-white -ml-1" />
              <span>Saving Changes...</span>
            </span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, defineProps, defineEmits } from "vue";
  import {
    X,
    PlusCircle,
    Trash2,
    Paperclip,
    User,
    Shield,
    MapPin,
    GraduationCap,
    MessageSquare,
  } from "lucide-vue-next";

  const props = defineProps({
    teacher: {
      type: Object,
      default: null,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["close", "save"]);

  const localTeacher = ref({});
  const photoPreview = ref(null);

  watch(
    () => props.teacher,
    (newTeacher) => {
      if (newTeacher) {
        localTeacher.value = JSON.parse(JSON.stringify(newTeacher));
        if (!localTeacher.value.qualifications) {
          localTeacher.value.qualifications = [];
        }
        photoPreview.value = localTeacher.value.photo || null;
      } else {
        localTeacher.value = {};
        photoPreview.value = null;
      }
    },
    { immediate: true, deep: true },
  );

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    } catch (e) {
      console.error("Error formatting date for input:", e);
      return "";
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        localTeacher.value.photo = e.target.result;
        photoPreview.value = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      localTeacher.value.photo = null;
      photoPreview.value = null;
    }
  };

  const handleAadhaarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        localTeacher.value.aadhaarCard = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      localTeacher.value.aadhaarCard = null;
    }
  };

  const handleQualificationUpload = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (localTeacher.value.qualifications[index]) {
          localTeacher.value.qualifications[index].fileUrl = e.target.result;
        }
      };
      reader.readAsDataURL(file);
    } else {
      if (localTeacher.value.qualifications[index]) {
        localTeacher.value.qualifications[index].fileUrl = null;
      }
    }
  };

  const addQualification = () => {
    if (!localTeacher.value.qualifications) {
      localTeacher.value.qualifications = [];
    }
    localTeacher.value.qualifications.push({
      type: "",
      institution: "",
      year: "",
      fileUrl: null,
    });
  };

  const removeQualification = (index) => {
    localTeacher.value.qualifications.splice(index, 1);
  };

  const handleSave = () => {
    emit("save", {
      teacherData: localTeacher.value,
    });
  };
</script>

<style scoped>
  .scale-95 {
    transform: scale(0.97);
  }
  .opacity-0 {
    opacity: 0;
  }
  .scale-100 {
    transform: scale(1);
  }
  .opacity-100 {
    opacity: 1;
  }
</style>
